import { useFormik } from "formik";
import { get, isEmpty } from "lodash";
import { useTranslation } from "react-i18next";
import { NAME_SPACES } from "shared/locales/constants";
import { CONTACT_POINT_SYSTEM, GENDER } from "utils/constants";
import { bindInputProps } from "utils/helpers/input";
import ActionButtons from "../Components/ActionButtons";
import DataProtection from "../Components/DataProtection";
import Header from "../Components/Header";
import Input from "../Components/Input";
import Layout from "../Components/Layout";
import validation from "./validation";

function View({ contact, onNext, onBack, locked, translate }) {
  const { t } = useTranslation();

  const { handleSubmit, values, setFieldValue, ...formProps } = useFormik({
    initialValues: {
      ...contact,
      acceptContact: get(contact, "acceptContact", false),
      user: {
        ...get(contact, "user", {}),
        gender: get(contact, "user.gender") || GENDER.MALE,
      },
    },
    onSubmit: onNext,
    validationSchema: validation(
      t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, { returnObjects: true }),
    ),
  });

  const isFormValid =
    get(values, "acceptContact") &&
    get(values, "user.firstname") &&
    get(values, "user.lastname") &&
    get(values, `telecoms.${CONTACT_POINT_SYSTEM.EMAIL}.value`) &&
    get(values, `telecoms.${CONTACT_POINT_SYSTEM.PHONE}.value`) &&
    isEmpty(formProps.errors);

  return (
    <Layout>
      <Header
        title={translate?.HEADER?.TITLE || "Dites-nous en un peu plus sur vous"}
        highlight={translate?.HEADER?.HIGHLIGHT || "plus sur vous"}
        subtitle={translate?.HEADER?.SUBTITLE || "Informations complémentaires"}
      />

      <div className="miltis-form">
        <div className="miltis-form__container">
          {/* Civilité */}
          <div className="miltis-form__civilite">
            <Input
              label={translate?.FORM?.CIVILITE?.LABEL || "Civilité"}
              type="radio"
              row
              values={[
                {
                  value: GENDER.MALE,
                  label: translate?.FORM?.CIVILITE?.MALE || "Homme",
                },
                {
                  value: GENDER.FEMALE,
                  label: translate?.FORM?.CIVILITE?.FEMALE || "Femme",
                },
              ]}
              {...bindInputProps({
                name: "user.gender",
                values,
                ...formProps,
              })}
              onChange={(value) => setFieldValue("user.gender", value)}
            />
          </div>

          {/* Row 1: Nom | Prénom */}
          <div className="miltis-form__row">
            <Input
              placeholder={
                translate?.FORM?.LASTNAME?.PLACEHOLDER || "ex : Doe"
              }
              label={translate?.FORM?.LASTNAME?.LABEL || "Nom"}
              {...bindInputProps({
                name: "user.lastname",
                values,
                ...formProps,
              })}
              disabled={locked}
              onChange={({ target: { value } }) => {
                setFieldValue("user.lastname", value);
              }}
            />
            <Input
              placeholder={
                translate?.FORM?.FIRSTNAME?.PLACEHOLDER || "ex : John"
              }
              label={translate?.FORM?.FIRSTNAME?.LABEL || "Prénom"}
              {...bindInputProps({
                name: "user.firstname",
                values,
                ...formProps,
              })}
              disabled={locked}
              onChange={({ target: { value } }) => {
                setFieldValue("user.firstname", value);
              }}
            />
          </div>

          {/* Row 2: N° de téléphone | Adresse email */}
          <div className="miltis-form__row">
            <Input
              placeholder={
                translate?.FORM?.PHONE?.PLACEHOLDER || "ex : 06 12 34 56 78"
              }
              label={translate?.FORM?.PHONE?.LABEL || "N° de téléphone"}
              {...bindInputProps({
                name: `telecoms.${CONTACT_POINT_SYSTEM.PHONE}.value`,
                values,
                ...formProps,
              })}
              disabled={locked}
              onChange={({ target: { value } }) => {
                setFieldValue(
                  `telecoms.${CONTACT_POINT_SYSTEM.PHONE}.value`,
                  value,
                );
              }}
            />
            <Input
              placeholder={
                translate?.FORM?.EMAIL?.PLACEHOLDER || "ex : johndoe@yahoo.fr"
              }
              label={translate?.FORM?.EMAIL?.LABEL || "Adresse email"}
              {...bindInputProps({
                name: `telecoms.${CONTACT_POINT_SYSTEM.EMAIL}.value`,
                values,
                ...formProps,
              })}
              disabled={locked}
              onChange={({ target: { value } }) => {
                setFieldValue(
                  `telecoms.${CONTACT_POINT_SYSTEM.EMAIL}.value`,
                  value,
                );
              }}
            />
          </div>

          {/* Consent checkbox */}
          <Input
            type="checkbox"
            variant="consent"
            checked={get(values, "acceptContact", false)}
            onChange={(checked) => setFieldValue("acceptContact", checked)}
          >
            <span
              dangerouslySetInnerHTML={{
                __html:
                  translate?.CONSENT?.TEXT ||
                  "En cliquant sur \"<strong>Découvrir mes offres</strong>\" j'accepte qu'un conseiller Miltis (Mutuelle soumise au Livre II du Code de la mutualité, immatriculée au répertoire SIRENE sous le n° 417 934 817) ou qu'un conseiller ADM Value (ORIAS 521 671 149) mandataire de la Mutuelle Miltis, me contacte dans le cadre d'une proposition de souscription d'un contrat d'assurance.",
              }}
            />
          </Input>
          <ActionButtons
            onNext={handleSubmit}
            onBack={onBack}
            nextText={
              translate?.BUTTON?.DISCOVER_OFFERS || "Découvrir mes offres"
            }
            nextProps={{ disabled: !isFormValid }}
          />

          <DataProtection
            paragraphs={[
              translate?.DATA_PROTECTION?.PARAGRAPH_1 ||
                "Conformément au Règlement (UE) 2016/679 du Parlement européen et du Conseil du 27 avril 2016, et à la Loi « Informatique et Libertés » n°78-17 du 6 janvier 1978 modifiée, Miltis vous informe que vos informations personnelles font l'objet d'un traitement. En tant que responsable de traitement, Miltis les utilise pour vous fournir un service de comparaison tarifaire, permettre l'établissement de devis, assurer un suivi des dossiers, évaluer la satisfaction, et améliorer l'expérience utilisateur relative aux services proposés sur le site.",
              translate?.DATA_PROTECTION?.PARAGRAPH_2 ||
                "Enfin, vos données peuvent, avec votre consentement, être utilisées à des fins de prospection commerciale par Miltis, mutuelle soumise au Livre II du Code de la mutualité, immatriculée au répertoire SIRENE sous le n° 417 934 817 ainsi que par ADM Value (ORIAS 521 671 149), mandataire de la Mutuelle Miltis (SIREN 417 934 817).",
              translate?.DATA_PROTECTION?.PARAGRAPH_3 ||
                "Vos données seront conservées pour des durées qui varient en fonction des finalités susvisées, et au maximum pendant la durée de la relation contractuelle, à laquelle s'ajoutent les durées de prescription légale en vigueur.",
              translate?.DATA_PROTECTION?.PARAGRAPH_4 ||
                "Vos données personnelles peuvent faire l'objet de transferts hors de l'Union Européenne encadrés par une décision d'adéquation rendue par la Commission Européenne ou par des clauses contractuelles type dictées par les autorités Européennes.",
              translate?.DATA_PROTECTION?.PARAGRAPH_5 ||
                "Lors de la collecte des informations, vous êtes informés du caractère obligatoire ou facultatif des réponses à fournir. La communication des informations obligatoires est requise pour pouvoir utiliser les services proposés par Miltis.",
              translate?.DATA_PROTECTION?.PARAGRAPH_6 ||
                "Vous disposez d'un droit d'accès, de rectification, de limitation de traitement, d'opposition au traitement, d'effacement et de portabilité des données vous concernant. Ces requêtes peuvent être adressées à notre Délégué à la protection des données en utilisant l'adresse suivante : contact-cnil@miltis.fr. Vous disposez également du droit d'introduire une réclamation auprès de la CNIL à l'adresse suivante : Commission Nationale Informatique et Libertés – 3 place de Fontenoy, 75007 Paris.",
            ]}
          />
        </div>
      </div>
    </Layout>
  );
}

export default View;
